import { unifiedApi } from '@/lib/api-client'
import type {
  AtomicRule,
  CreateAtomicRuleRequest,
  UpdateAtomicRuleRequest,
  AtomicRuleFilter,
  RuleImportBatch,
  EvalContext,
  EvalResult,
  AtomicRulesMetadata,
  AtomicRuleStats,
  CSVValidationResult,
  AtomicRulesListResponse,
  AtomicRuleResponse,
  ImportBatchResponse,
  EvaluationResponse,
  MetadataResponse,
  StatsResponse,
  RuleTemplate,
  GroupedRulesResponse,
  GroupedRulesData,
  PolicyRuleCounts,
  RuleWriteResult,
} from '@/types/atomic-rules'
import { ruleWarningsOf } from './rule-vocabulary.service'

class AtomicRulesService {
  private getEndpoint(path: string): string {
    // Paths must NOT start with a slash when using prefixUrl (Ky requirement)
    const cleanPath = path.startsWith('/') ? path.slice(1) : path
    return `governance/atomic-rules${cleanPath ? '/' + cleanPath : ''}`
  }

  // CRUD Operations

  /**
   * List atomic rules with optional filtering
   */
  async listRules(filter?: AtomicRuleFilter, signal?: AbortSignal): Promise<AtomicRulesListResponse['data']> {
    const params = new URLSearchParams()
    if (filter?.enabled !== undefined) params.set('enabled', String(filter.enabled))
    if (filter?.action) params.set('action', filter.action)
    if (filter?.conditionField) params.set('condition_field', filter.conditionField)
    if (filter?.search) params.set('search', filter.search)
    if (filter?.importBatchId) params.set('import_batch_id', filter.importBatchId)
    if (filter?.missionId) params.set('mission_id', filter.missionId)
    if (filter?.regulationId) params.set('regulation_id', filter.regulationId)
    if (filter?.sortBy) params.set('sort_by', filter.sortBy)
    if (filter?.sortOrder) params.set('sort_order', filter.sortOrder)
    if (filter?.limit) params.set('limit', String(filter.limit))
    if (filter?.offset) params.set('offset', String(filter.offset))
    if (filter?.includeGlobal !== undefined) params.set('include_global', String(filter.includeGlobal))
    if (filter?.includeSystem !== undefined) params.set('include_system', String(filter.includeSystem))
    if (filter?.placementScope) params.set('placement_scope', filter.placementScope)
    if (filter?.priority !== undefined) params.set('priority', String(filter.priority))
    if (filter?.boundOnly !== undefined) params.set('bound_only', String(filter.boundOnly))
    if (filter?.orphanedOnly !== undefined) params.set('orphaned_only', String(filter.orphanedOnly))
    if (filter?.searchTokens?.length) params.set('search_tokens', JSON.stringify(filter.searchTokens))

    const response = await unifiedApi.get<AtomicRulesListResponse>(
      this.getEndpoint('') + (params.toString() ? `?${params.toString()}` : ''), undefined, { signal },
    )
    return response.data
  }

  async getPolicyCounts(signal?: AbortSignal): Promise<PolicyRuleCounts> {
    const response = await unifiedApi.get<{ data: PolicyRuleCounts }>(this.getEndpoint('stats'), { view: 'policy' }, { signal })
    if (!response.data || !['total', 'regulatory', 'organization', 'mission', 'orphaned'].every((key) => Number.isInteger(response.data[key as keyof PolicyRuleCounts]) && response.data[key as keyof PolicyRuleCounts] >= 0)) {
      throw new Error('Invalid policy rule counts')
    }
    return response.data
  }

  /**
   * Get rules organized by groups (system, organization, mission)
   * This provides a hierarchical view of all applicable rules
   */
  async getGroupedRules(): Promise<GroupedRulesData> {
    const response = await unifiedApi.get<GroupedRulesResponse>(this.getEndpoint('grouped'))
    return response.data
  }

  /**
   * Get a single rule by ID
   */
  async getRule(id: number, signal?: AbortSignal): Promise<AtomicRule> {
    const response = await unifiedApi.get<AtomicRuleResponse>(this.getEndpoint(String(id)), undefined, { signal })
    return response.data
  }

  /**
   * Create a new atomic rule
   */
  async createRule(rule: CreateAtomicRuleRequest): Promise<AtomicRule> {
    const response = await unifiedApi.post<AtomicRuleResponse, CreateAtomicRuleRequest>(
      this.getEndpoint(''),
      rule
    )
    return response.data
  }

  /**
   * Update an existing rule
   */
  async updateRule(id: number, updates: UpdateAtomicRuleRequest): Promise<AtomicRule> {
    const response = await unifiedApi.put<AtomicRuleResponse, UpdateAtomicRuleRequest>(
      this.getEndpoint(String(id)),
      updates
    )
    return response.data
  }

  /**
   * Create a rule and keep the server's vocabulary warnings (agion/Agion#2859).
   * A refusal rejects with the API failure; ruleViolationsOf reads it.
   */
  async createRuleWithWarnings(rule: CreateAtomicRuleRequest): Promise<RuleWriteResult> {
    const response = await unifiedApi.post<AtomicRuleResponse, CreateAtomicRuleRequest>(this.getEndpoint(''), rule)
    return { rule: response.data, warnings: ruleWarningsOf(response) }
  }

  /** Update a rule and keep the server's vocabulary warnings (agion/Agion#2859). */
  async updateRuleWithWarnings(id: number, updates: UpdateAtomicRuleRequest): Promise<RuleWriteResult> {
    const response = await unifiedApi.put<AtomicRuleResponse, UpdateAtomicRuleRequest>(this.getEndpoint(String(id)), updates)
    return { rule: response.data, warnings: ruleWarningsOf(response) }
  }

  /**
   * Delete a rule
   */
  async deleteRule(id: number): Promise<void> {
    await unifiedApi.delete<{ success: boolean }>(this.getEndpoint(String(id)))
  }

  /**
   * Toggle rule enabled/disabled
   */
  async toggleRule(id: number, enabled: boolean): Promise<void> {
    await unifiedApi.patch<{ success: boolean }, { enabled: boolean }>(
      this.getEndpoint(`${id}/toggle`),
      { enabled }
    )
  }

  // Import/Export Operations

  /**
   * Import rules from CSV file
   */
  async importCSV(file: File): Promise<RuleImportBatch> {
    const response = await unifiedApi.uploadFile<ImportBatchResponse>(
      this.getEndpoint('import/csv'),
      file
    )
    return response.data
  }

  /**
   * Export all rules to CSV
   */
  async exportCSV(): Promise<Blob> {
    // Note: UnifiedAPIClient.get only accepts path and searchParams
    // For blob responses, we need to use the underlying ky client directly
    // This is a workaround until uploadFile supports other response types
    const response = await unifiedApi.get<Blob>(this.getEndpoint('export/csv'))
    return response
  }

  /**
   * Import rules from a predefined template
   */
  async importTemplate(template: RuleTemplate): Promise<RuleImportBatch> {
    const response = await unifiedApi.post<ImportBatchResponse, Record<string, never>>(
      this.getEndpoint(`import/template/${template}`),
      {}
    )
    return response.data
  }

  /**
   * Validate a CSV file without importing
   */
  async validateCSV(file: File): Promise<CSVValidationResult> {
    const response = await unifiedApi.uploadFile<{ success: boolean; data: CSVValidationResult }>(
      this.getEndpoint('validate/csv'),
      file
    )
    return response.data
  }

  /**
   * Delete all rules from an import batch
   */
  async deleteBatch(batchId: string): Promise<{ deleted: number }> {
    const response = await unifiedApi.delete<{ success: boolean; data: { deleted: number } }>(
      this.getEndpoint(`batch/${batchId}`)
    )
    return response.data
  }

  /**
   * Get CSV template for import
   */
  async getCSVTemplate(): Promise<string> {
    // Note: UnifiedAPIClient.get only accepts path and searchParams
    const response = await unifiedApi.get<string>(this.getEndpoint('template/csv'))
    return response
  }

  // Evaluation Operations

  /**
   * Evaluate rules against a context
   */
  async evaluate(context: EvalContext): Promise<EvalResult> {
    const response = await unifiedApi.post<EvaluationResponse, EvalContext>(
      this.getEndpoint('evaluate'),
      context
    )
    return response.data
  }

  /**
   * Test a rule without saving it
   */
  async testRule(
    rule: CreateAtomicRuleRequest,
    context: EvalContext
  ): Promise<{ matched: boolean; reason: string; action: string }> {
    const response = await unifiedApi.post<
      { success: boolean; data: { matched: boolean; reason: string; action: string } },
      { rule: CreateAtomicRuleRequest; context: EvalContext }
    >(this.getEndpoint('test'), { rule, context })
    return response.data
  }

  // Metadata Operations

  /**
   * Get metadata for building rules (fields, operators, tiers, actions)
   */
  async getMetadata(signal?: AbortSignal): Promise<AtomicRulesMetadata> {
    const response = await unifiedApi.get<MetadataResponse>(this.getEndpoint('metadata'), undefined, { signal })
    return response.data
  }

  /**
   * Get rule statistics for the organization
   */
  async getStats(includeSystem = true): Promise<AtomicRuleStats> {
    const params = includeSystem ? '' : '?include_system=false'
    const response = await unifiedApi.get<StatsResponse>(this.getEndpoint('stats') + params)
    return response.data
  }

  /**
   * List import batch history
   */
  async listImportBatches(limit?: number): Promise<RuleImportBatch[]> {
    const params = limit ? `?limit=${limit}` : ''
    const response = await unifiedApi.get<{ success: boolean; data: RuleImportBatch[] }>(
      this.getEndpoint('import-batches') + params
    )
    return response.data
  }

  // Helper methods for UI

  /**
   * Download CSV template as a file
   */
  async downloadCSVTemplate(): Promise<void> {
    const template = await this.getCSVTemplate()
    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'atomic_rules_template.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /**
   * Download exported rules as a file
   */
  async downloadExport(): Promise<void> {
    const blob = await this.exportCSV()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `atomic_rules_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /**
   * Quick enable/disable a rule
   */
  async enable(id: number): Promise<void> {
    await this.toggleRule(id, true)
  }

  async disable(id: number): Promise<void> {
    await this.toggleRule(id, false)
  }

  /**
   * Bulk enable/disable rules
   */
  async bulkToggle(ids: number[], enabled: boolean): Promise<void> {
    await Promise.all(ids.map((id) => this.toggleRule(id, enabled)))
  }

  /**
   * Bulk delete rules
   */
  async bulkDelete(ids: number[]): Promise<void> {
    await Promise.all(ids.map((id) => this.deleteRule(id)))
  }
}

export const atomicRulesService = new AtomicRulesService()
